import type { ConditionClinicalStatus } from '@seen/core/models/conditions/constants'
import type { Diagnosis } from '@seen/core/models/conditions/Diagnosis'
import { sortBy } from 'lodash-es'

export type ConditionActivityEvent =
  | {
      kind: 'created'
      diagnosis: Diagnosis
      key: string
    }
  | {
      kind: 'removed'
      diagnosis: Diagnosis
      key: string
    }
  | {
      kind: 'changed-name'
      diagnosis: Diagnosis
      key: string
      previousName: string
      currentName: string
      previousIcd10Codes: string[]
      currentIcd10Codes: string[]
    }
  | {
      kind: 'changed-status'
      diagnosis: Diagnosis
      key: string
      previousStatus: ConditionClinicalStatus
      currentStatus: ConditionClinicalStatus
    }
  | {
      kind: 'changed-onset-date'
      diagnosis: Diagnosis
      key: string
      previousOnsetDate: string | null
      currentOnsetDate: string | null
    }

export function buildConditionActivity(
  versions: Diagnosis[]
): ConditionActivityEvent[] {
  const sortedVersions = sortBy(versions, (diagnosis) => diagnosis.lastUpdated)

  return sortedVersions.reduce<ConditionActivityEvent[]>((acc, curr, idx) => {
    if (idx === 0) {
      acc.push({
        kind: 'created',
        diagnosis: curr,
        key: `${curr.versionId}-created`,
      })
      return acc
    }

    const prev = sortedVersions[idx - 1]
    if (!prev.isDeleted && curr.isDeleted) {
      acc.push({
        kind: 'removed',
        diagnosis: curr,
        key: `${curr.versionId}-removed`,
      })
      return acc
    }

    const previousIcd10Codes = sortedIcd10Codes(prev)
    const currentIcd10Codes = sortedIcd10Codes(curr)

    const nameChanged = curr.name !== prev.name

    if (nameChanged) {
      acc.push({
        kind: 'changed-name',
        diagnosis: curr,
        key: `${curr.versionId}-name`,
        previousName: prev.name,
        currentName: curr.name,
        previousIcd10Codes,
        currentIcd10Codes,
      })
    }

    if (curr.clinicalStatus !== prev.clinicalStatus) {
      acc.push({
        kind: 'changed-status',
        diagnosis: curr,
        key: `${curr.versionId}-status`,
        previousStatus: prev.clinicalStatus,
        currentStatus: curr.clinicalStatus,
      })
    }

    if (curr.onsetDateString !== prev.onsetDateString) {
      acc.push({
        kind: 'changed-onset-date',
        diagnosis: curr,
        key: `${curr.versionId}-onset-date`,
        previousOnsetDate: prev.onsetDateString,
        currentOnsetDate: curr.onsetDateString,
      })
    }

    return acc
  }, [])
}

function sortedIcd10Codes(diagnosis: Diagnosis): string[] {
  return sortBy(diagnosis.icd10Codes)
}
